import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { IdCard, Loader2, Sparkles, CheckCircle2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import FilePickerButton from "../ui/FilePickerButton";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TIPI_DOCUMENTO = [
  { codice: "IDELE", descrizione: "CARTA IDENTITA' ELETTRONICA", dueFacciate: true },
  { codice: "IDENT", descrizione: "CARTA DI IDENTITA'", dueFacciate: true },
  { codice: "PASOR", descrizione: "PASSAPORTO ORDINARIO", dueFacciate: false },
  { codice: "PASSE", descrizione: "PASSAPORTO SERVIZIO", dueFacciate: false },
  { codice: "PASDI", descrizione: "PASSAPORTO DIPLOMATICO", dueFacciate: false },
  { codice: "PATEN", descrizione: "PATENTE DI GUIDA", dueFacciate: true },
  { codice: "PERMO", descrizione: "PERMESSO DI SOGGIORNO ORDINARIO", dueFacciate: true },
  { codice: "PERME", descrizione: "PERMESSO DI SOGGIORNO ELETTRONICO", dueFacciate: true },
  { codice: "DOCVI", descrizione: "DOCUMENTO DI VIAGGIO", dueFacciate: false },
];

const FIELD_LABELS = {
  nome: "Nome",
  cognome: "Cognome",
  data_nascita: "Data di nascita",
  sesso: "Sesso",
  codice_fiscale: "Codice fiscale",
  comune_nascita_label: "Comune di nascita",
  stato_nascita_label: "Stato di nascita",
  cittadinanza_label: "Cittadinanza",
  tipo_documento_label: "Tipo documento",
  numero_documento: "Numero documento",
  data_scadenza_documento: "Scadenza documento",
  luogo_rilascio_documento: "Luogo rilascio",
  indirizzo: "Indirizzo",
  cap: "CAP",
  citta: "Città",
  provincia: "Provincia",
};

const ALLOWED_EXT = [".pdf", ".jpg", ".jpeg", ".png", ".webp", ".gif"];

function formatDateIt(iso) {
  if (!iso) return "—";
  const d = new Date(`${iso.slice(0, 10)}T12:00:00`);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString("it-IT");
}

function validateFile(file) {
  if (!file) return null;
  const ext = `.${file.name.split(".").pop().toLowerCase()}`;
  if (!ALLOWED_EXT.includes(ext)) {
    return `Formato non supportato (${ext}). Usa JPG, PNG o PDF.`;
  }
  if (file.size > 50 * 1024 * 1024) {
    return "File troppo grande (max 50 MB)";
  }
  return null;
}

export default function CompilaAnagraficaDaIdentitaDialog({
  open,
  onOpenChange,
  fascicoloId,
  token,
  onApply,
}) {
  const [tipoDocumento, setTipoDocumento] = useState("IDELE");
  const [fronteFile, setFronteFile] = useState(null);
  const [retroFile, setRetroFile] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const tipoInfo = TIPI_DOCUMENTO.find((t) => t.codice === tipoDocumento);
  const richiedeRetro = !!tipoInfo?.dueFacciate;

  const previewFields = useMemo(() => {
    if (!result?.fields) return [];
    return Object.entries(FIELD_LABELS)
      .map(([key, label]) => {
        const raw = result.fields[key];
        if (!raw || !String(raw).trim()) return null;
        let value = String(raw).trim();
        if (key === "data_nascita" || key === "data_scadenza_documento") {
          value = formatDateIt(value);
        }
        if (key === "sesso") {
          value = value === "M" ? "Maschio" : value === "F" ? "Femmina" : value;
        }
        return { key, label, value };
      })
      .filter(Boolean);
  }, [result]);

  const reset = () => {
    setTipoDocumento("IDELE");
    setFronteFile(null);
    setRetroFile(null);
    setResult(null);
    setAnalyzing(false);
  };

  const handleClose = (value) => {
    if (!value) reset();
    onOpenChange(value);
  };

  useEffect(() => {
    if (!open) return;
    setResult(null);
  }, [open, tipoDocumento]);

  const handleAnalyze = async () => {
    if (!fronteFile) {
      toast.error("Carica almeno il fronte del documento");
      return;
    }
    const fronteErr = validateFile(fronteFile);
    if (fronteErr) return toast.error(fronteErr);
    if (retroFile) {
      const retroErr = validateFile(retroFile);
      if (retroErr) return toast.error(retroErr);
    }

    const formData = new FormData();
    formData.append("fronte", fronteFile);
    if (retroFile) formData.append("retro", retroFile);
    formData.append("tipo_documento", tipoDocumento);

    setAnalyzing(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/fascicoli-assistenziali/${fascicoloId}/anagrafica/extract-identita`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      setResult(data);
      if (data.warnings?.length) {
        toast.message("Analisi completata con avvisi", {
          description: data.warnings.join(" · "),
        });
      } else {
        toast.success("Dati estratti dal documento");
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante l'analisi del documento");
      setResult(null);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleApply = () => {
    if (!result?.fields) return;
    onApply?.({
      fields: result.fields,
      documents: result.documents || [],
    });
    handleClose(false);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="dialog-compila-da-identita">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-violet-600" />
            Compila anagrafica da documento identità
          </DialogTitle>
          <DialogDescription>
            Carica il documento (fronte e, se serve, retro). L&apos;intelligenza artificiale legge i dati
            e li propone per la revisione prima di applicarli al fascicolo.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">
          <div className="space-y-2">
            <Label>Tipo documento</Label>
            <Select value={tipoDocumento} onValueChange={setTipoDocumento}>
              <SelectTrigger data-testid="select-tipo-documento-identita">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {TIPI_DOCUMENTO.map((doc) => (
                  <SelectItem key={doc.codice} value={doc.codice}>
                    {doc.descrizione}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {richiedeRetro ? (
              <p className="text-xs text-slate-500">
                Per questo tipo di documento è consigliato caricare anche il retro (codice fiscale e residenza).
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Fronte documento *</Label>
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-6">
                <span className="text-xs text-slate-600 text-center">
                  {fronteFile ? fronteFile.name : "JPG, PNG o PDF"}
                </span>
                <FilePickerButton
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
                  onChange={(e) => setFronteFile(e.target.files?.[0] || null)}
                  fileLabel="Carica fronte"
                  photoLabel="Fotografa fronte"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Retro documento {richiedeRetro ? "(consigliato)" : "(opzionale)"}</Label>
              <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50/80 px-4 py-6">
                <IdCard className="w-5 h-5 text-slate-400" />
                <span className="text-xs text-slate-600 text-center">
                  {retroFile ? retroFile.name : "JPG, PNG o PDF"}
                </span>
                <FilePickerButton
                  accept=".pdf,.jpg,.jpeg,.png,.webp,.gif"
                  onChange={(e) => setRetroFile(e.target.files?.[0] || null)}
                  fileLabel="Carica retro"
                  photoLabel="Fotografa retro"
                />
              </div>
            </div>
          </div>

          {result?.fields ? (
            <div className="rounded-xl border border-violet-200 bg-violet-50/60 p-4 space-y-3">
              <div className="flex items-center gap-2 text-sm font-medium text-violet-900">
                <CheckCircle2 className="w-4 h-4" />
                Dati riconosciuti — verifica prima di applicare
              </div>
              {previewFields.length > 0 ? (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
                  {previewFields.map((item) => (
                    <div key={item.key}>
                      <dt className="text-xs text-slate-500">{item.label}</dt>
                      <dd className="font-medium text-slate-800 break-words">{item.value}</dd>
                    </div>
                  ))}
                </dl>
              ) : (
                <p className="text-sm text-slate-600">Nessun campo utilizzabile estratto.</p>
              )}
              {result.warnings?.length ? (
                <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                  {result.warnings.join(" · ")}
                </p>
              ) : null}
            </div>
          ) : null}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={() => handleClose(false)} disabled={analyzing}>
            Annulla
          </Button>
          {!result?.fields ? (
            <Button
              onClick={handleAnalyze}
              disabled={analyzing || !fronteFile}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="btn-analizza-identita"
            >
              {analyzing ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <Sparkles className="w-4 h-4 mr-1.5" />}
              Analizza con AI
            </Button>
          ) : (
            <Button
              onClick={handleApply}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="btn-applica-identita"
            >
              Applica al fascicolo
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
